import React from 'react';
import { Activity, FileText, Clock, Shield, Lock, DollarSign, TrendingUp } from 'lucide-react';
import { AuditLog, AudioGeneration } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface AnalyticsDashboardProps {
  auditLogs: AuditLog[];
  generations: AudioGeneration[];
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  auditLogs,
  generations,
}) => {
  const { currentUser } = useAuth();

  if (currentUser?.role !== 'SUPER_ADMIN') {
    return (
      <div className="p-8 rounded-2xl bg-white border border-slate-200 dark:bg-slate-900/60 dark:border-slate-800 text-center max-w-lg mx-auto space-y-3">
        <Lock className="w-8 h-8 text-amber-500 mx-auto" />
        <h2 className="text-base font-bold text-slate-900 dark:text-white">Super Admin Access Only</h2>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          Platform-wide provider costs, revenue telemetry, and system audit logs are restricted to the SaaS Platform Super Admin.
        </p>
      </div>
    );
  }

  const totalCharacters = generations.reduce((sum, g) => sum + g.characterCount, 0);
  const totalDurationSeconds = generations.reduce((sum, g) => sum + (g.durationSeconds || 0), 0);
  const totalProviderCost = generations.reduce((sum, g) => sum + g.estimatedCostUsd, 0);
  const totalCreditsBilled = generations.reduce((sum, g) => sum + (g.creditsBurned || 450), 0);

  // SaaS profit model: e.g. $0.0012 per credit billed vs raw provider cost
  const estimatedRevenue = totalCreditsBilled * 0.0012;
  const estimatedGrossProfit = Math.max(0, estimatedRevenue - totalProviderCost);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'CATEGORY_UPDATED':
        return 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30';
      case 'VOICE_RENAMED':
        return 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/30';
      case 'VOICE_CLONED':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
      case 'CONSENT_REVOKED':
        return 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30';
      case 'GENERATION_COMPLETED':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  return (
    <div className="space-y-8 w-full">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-500/30 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Super Admin Telemetry
          </span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
          <Activity className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          Platform Costs & Profit Telemetry
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Monitor raw AI provider infrastructure expenses, credit burn rates, gross margins, and system audit trails.
        </p>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Characters */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/80 dark:border-slate-800 space-y-2 dark:shadow-lg transition-colors">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">
              Characters Synthesized
            </span>
            <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {totalCharacters.toLocaleString()}
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">Across all church tenants</div>
        </div>

        {/* Audio Minutes */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/80 dark:border-slate-800 space-y-2 dark:shadow-lg transition-colors">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Audio Duration</span>
            <Clock className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {(totalDurationSeconds / 60).toFixed(1)} mins
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">
            {generations.length} total audio files generated
          </div>
        </div>

        {/* Underlying Provider Cost */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/80 dark:border-slate-800 space-y-2 dark:shadow-lg transition-colors">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Raw Provider Cost</span>
            <DollarSign className="w-4 h-4 text-amber-500 dark:text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            ${totalProviderCost.toFixed(2)} USD
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">ElevenLabs, Inworld, Google Cloud</div>
        </div>

        {/* Estimated Gross Margin */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/80 dark:border-slate-800 space-y-2 dark:shadow-lg transition-colors">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Gross Platform Margin</span>
            <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            ${estimatedGrossProfit.toFixed(2)} USD
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">
            {totalCreditsBilled.toLocaleString()} credits burned
          </div>
        </div>
      </div>

      {/* Provider Cost Breakdown Table */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/70 dark:border-slate-800 dark:shadow-xl overflow-hidden p-5 space-y-4 transition-colors">
        <h3 className="font-bold text-sm text-slate-900 dark:text-white">AI Provider Usage & Cost Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/60 space-y-1">
            <div className="text-xs text-amber-600 dark:text-amber-400 font-bold">ElevenLabs Turbo v2.5</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">${(totalProviderCost * 0.7).toFixed(2)} USD</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Devotional & Leadership Clones</div>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/60 space-y-1">
            <div className="text-xs text-indigo-600 dark:text-indigo-400 font-bold">Inworld AI Interactive</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">${(totalProviderCost * 0.2).toFixed(2)} USD</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Dynamic Evangelist Recaps</div>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/60 space-y-1">
            <div className="text-xs text-cyan-600 dark:text-cyan-400 font-bold">Google Cloud Neural Journey</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">${(totalProviderCost * 0.1).toFixed(2)} USD</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Standard Scripture Lectors</div>
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/70 dark:border-slate-800 dark:shadow-xl overflow-hidden transition-colors">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
            Platform Security & Audit Trail ({auditLogs.length} Events)
          </div>
          <div className="text-xs text-slate-500">Immutable Compliance Log</div>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${getActionBadge(
                      log.action,
                    )}`}
                  >
                    {log.action.replace('_', ' ')}
                  </span>
                  <span className="text-slate-900 dark:text-white font-medium">{log.details}</span>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <span>Actor: {log.actorEmail}</span>
                  <span>•</span>
                  <span>IP: {log.ipAddress}</span>
                </div>
              </div>

              <div className="text-[11px] text-slate-400 dark:text-slate-500 font-mono self-end sm:self-auto flex-shrink-0">
                {new Date(log.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                - {new Date(log.timestamp).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
